// components/project/EconomicLineInstallments.js
// Modal per gestione rate pagamento fornitori - Solo Admin/Administration

import React, { useState, useEffect, useCallback } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '../ui/dialog';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { Textarea } from '../ui/textarea';
import { toast } from 'sonner';
import { 
  Plus, Calendar, CreditCard, Loader2, X, AlertTriangle, Check,
  DollarSign, Clock, AlertCircle, Banknote, Edit, Trash2
} from 'lucide-react';
import api from '../../lib/api';

// Labels stati rata
const INSTALLMENT_STATUS_LABELS = {
  da_pagare: 'Da pagare',
  parziale: 'Parziale',
  pagata: 'Pagata',
  scaduta: 'Scaduta',
  annullata: 'Annullata'
};

const INSTALLMENT_STATUS_COLORS = {
  da_pagare: 'bg-gray-100 text-gray-700',
  parziale: 'bg-yellow-100 text-yellow-700',
  pagata: 'bg-green-100 text-green-700',
  scaduta: 'bg-red-100 text-red-700',
  annullata: 'bg-gray-200 text-gray-500 line-through'
};

const PAYMENT_STATUS_LABELS = {
  nessuna_rata: 'Nessuna rata',
  da_pagare: 'Da pagare',
  parziale: 'Parziale',
  pagato: 'Pagato',
  scaduto: 'Scaduto'
};

const PAYMENT_STATUS_COLORS = {
  nessuna_rata: 'bg-gray-100 text-gray-600',
  da_pagare: 'bg-blue-100 text-blue-700',
  parziale: 'bg-yellow-100 text-yellow-700',
  pagato: 'bg-green-100 text-green-700',
  scaduto: 'bg-red-100 text-red-700'
};

// Descrizioni suggerite
const SUGGESTED_DESCRIPTIONS = {
  lavori: ['Acconto lavori', 'SAL impresa', 'Avanzamento lavori', 'Saldo lavori'],
  forniture: ['Acconto ordine', 'Primo scarico', 'Secondo scarico', 'Saldo merce'],
  generici: ['Rata unica', 'Acconto', 'Saldo', 'Altro']
};

// Utility formattazione
const formatCurrency = (value) => {
  if (value === null || value === undefined) return '-';
  const n0 = Number(value);
  const n = isNaN(n0) ? value : (Math.round(n0 * 100) === 0 ? 0 : n0);
  return new Intl.NumberFormat('it-IT', {
    style: 'currency',
    currency: 'EUR'
  }).format(n);
};

const formatDate = (dateStr) => {
  if (!dateStr) return '-';
  try {
    return new Date(dateStr).toLocaleDateString('it-IT');
  } catch {
    return dateStr;
  }
};

// Parse input numerico italiano
const parseItalianNumber = (value) => {
  if (!value) return 0;
  const str = String(value).trim();
  // Rimuovi separatore migliaia (.) e sostituisci virgola decimale con punto
  const normalized = str.replace(/\./g, '').replace(',', '.');
  const num = parseFloat(normalized);
  return isNaN(num) ? 0 : num;
};

// Badge stato pagamento per tabella
export const PaymentStatusBadge = ({ status, count, paidTotal, remainingTotal }) => {
  if (count === 0) {
    return (
      <span className="text-xs text-gray-400">-</span>
    );
  }
  
  return (
    <Badge className={`text-xs ${PAYMENT_STATUS_COLORS[status] || 'bg-gray-100'}`}>
      {PAYMENT_STATUS_LABELS[status] || status}
    </Badge>
  );
};

// Colonne rate per tabella principale
export const InstallmentColumns = ({ summary }) => {
  if (!summary || summary.installments_count === 0) {
    return {
      rate: <span className="text-gray-400">-</span>,
      pagato: <span className="text-gray-400">-</span>,
      residuo: <span className="text-gray-400">-</span>,
      stato: <span className="text-gray-400">-</span>
    };
  }
  
  return {
    rate: <span className="font-medium">{summary.installments_count}</span>,
    pagato: <span className="text-green-600">{formatCurrency(summary.installments_paid_total)}</span>,
    residuo: <span className={summary.installments_remaining_total > 0 ? 'text-orange-600' : 'text-gray-600'}>
      {formatCurrency(summary.installments_remaining_total)}
    </span>,
    stato: <PaymentStatusBadge 
      status={summary.payment_status} 
      count={summary.installments_count}
      paidTotal={summary.installments_paid_total}
      remainingTotal={summary.installments_remaining_total}
    />
  };
};

// Modal principale gestione rate
const EconomicLineInstallmentsModal = ({ 
  isOpen, 
  onClose, 
  projectId, 
  line,
  linkedDocuments = [],
  onInstallmentChange
}) => {
  const [installments, setInstallments] = useState([]);
  const [summary, setSummary] = useState(null);
  const [lineInfo, setLineInfo] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  
  // Stati form
  const [showAddForm, setShowAddForm] = useState(false);
  const [editingInstallment, setEditingInstallment] = useState(null);
  const [showPayForm, setShowPayForm] = useState(null);
  
  // Form fields
  const [formData, setFormData] = useState({
    description: '',
    amount: '',
    due_date: '',
    paid_amount: '',
    paid_at: '',
    linked_document_id: '',
    invoice_number: '',
    invoice_date: '',
    notes: ''
  });
  
  // Pay form
  const [payAmount, setPayAmount] = useState('');
  const [payDate, setPayDate] = useState('');

  // Carica dati
  const fetchData = useCallback(async () => {
    if (!line?.id || !projectId) return;
    
    try {
      setLoading(true);
      const response = await api.get(`/projects/${projectId}/economic-lines/${line.id}/installments`);
      setInstallments(response.data.installments || []);
      setSummary(response.data.summary || null);
      setLineInfo(response.data.line || null);
    } catch (error) {
      console.error('Error fetching installments:', error);
      if (error.response?.status !== 404) {
        toast.error('Errore nel caricamento rate');
      }
    } finally {
      setLoading(false);
    }
  }, [projectId, line?.id]);

  useEffect(() => {
    if (isOpen && line?.id) {
      fetchData();
    }
  }, [isOpen, line?.id, fetchData]);

  // Reset form
  const resetForm = () => {
    setFormData({
      description: '',
      amount: '',
      due_date: '',
      paid_amount: '',
      paid_at: '',
      linked_document_id: '',
      invoice_number: '',
      invoice_date: '',
      notes: ''
    });
    setEditingInstallment(null);
    setShowAddForm(false);
  };

  // Apri form modifica
  const openEditForm = (inst) => {
    setEditingInstallment(inst);
    setFormData({
      description: inst.description || '',
      amount: String(inst.amount || ''),
      due_date: inst.due_date ? inst.due_date.split('T')[0] : '',
      paid_amount: String(inst.paid_amount || ''),
      paid_at: inst.paid_at ? inst.paid_at.split('T')[0] : '',
      linked_document_id: inst.linked_document_id || '',
      invoice_number: inst.invoice_number || '',
      invoice_date: inst.invoice_date ? inst.invoice_date.split('T')[0] : '',
      notes: inst.notes || ''
    });
    setShowAddForm(true);
  };

  // Salva rata (create/update)
  const handleSaveInstallment = async () => {
    if (!formData.description.trim()) {
      toast.error('Inserisci una descrizione');
      return;
    }
    
    const amount = parseItalianNumber(formData.amount);
    const paidAmount = parseItalianNumber(formData.paid_amount);
    
    if (amount <= 0) {
      toast.error('Inserisci un importo valido');
      return;
    }
    
    if (paidAmount > amount) {
      toast.error('L\'importo pagato non può superare l\'importo della rata');
      return;
    }
    
    try {
      setSaving(true);
      
      const payload = {
        description: formData.description.trim(),
        amount: amount,
        due_date: formData.due_date || null,
        paid_amount: paidAmount,
        paid_at: formData.paid_at || null,
        linked_document_id: formData.linked_document_id || null,
        invoice_number: formData.invoice_number || null,
        invoice_date: formData.invoice_date || null,
        notes: formData.notes || null
      };
      
      if (editingInstallment) {
        // Update
        await api.put(
          `/projects/${projectId}/economic-lines/${line.id}/installments/${editingInstallment.id}`,
          payload
        );
        toast.success('Rata aggiornata');
      } else {
        // Create
        await api.post(
          `/projects/${projectId}/economic-lines/${line.id}/installments`,
          payload
        );
        toast.success('Rata creata');
      }
      
      resetForm();
      fetchData();
      if (onInstallmentChange) onInstallmentChange();
    } catch (error) {
      console.error('Save error:', error);
      toast.error(error.response?.data?.detail || 'Errore durante il salvataggio');
    } finally {
      setSaving(false);
    }
  };

  // Crea rata unica default
  const handleCreateDefault = async () => {
    try {
      setSaving(true);
      await api.post(`/projects/${projectId}/economic-lines/${line.id}/installments/create-default`);
      toast.success('Rata unica creata');
      fetchData();
      if (onInstallmentChange) onInstallmentChange();
    } catch (error) {
      console.error('Create default error:', error);
      toast.error(error.response?.data?.detail || 'Errore nella creazione');
    } finally {
      setSaving(false);
    }
  };

  // Segna pagata
  const handleMarkPaid = async (inst) => {
    const amount = parseItalianNumber(payAmount);
    
    if (amount <= 0) {
      toast.error('Inserisci un importo valido');
      return;
    }
    
    if (amount > inst.amount) {
      toast.error('L\'importo pagato non può superare l\'importo della rata');
      return;
    }
    
    try {
      setSaving(true);
      
      const formData = new FormData();
      formData.append('paid_amount', String(amount));
      if (payDate) formData.append('paid_at', payDate);
      
      await api.post(
        `/projects/${projectId}/economic-lines/${line.id}/installments/${inst.id}/mark-paid`,
        formData
      );
      
      toast.success(amount >= inst.amount ? 'Rata pagata completamente' : 'Pagamento parziale registrato');
      setShowPayForm(null);
      setPayAmount('');
      setPayDate('');
      fetchData();
      if (onInstallmentChange) onInstallmentChange();
    } catch (error) {
      console.error('Mark paid error:', error);
      toast.error(error.response?.data?.detail || 'Errore durante la registrazione');
    } finally {
      setSaving(false);
    }
  };

  // Annulla rata
  const handleCancelInstallment = async (inst) => {
    if (!window.confirm(`Sei sicuro di voler annullare la rata "${inst.description}"?`)) {
      return;
    }
    
    try {
      await api.delete(`/projects/${projectId}/economic-lines/${line.id}/installments/${inst.id}`);
      toast.success('Rata annullata');
      fetchData();
      if (onInstallmentChange) onInstallmentChange();
    } catch (error) {
      console.error('Cancel error:', error);
      toast.error(error.response?.data?.detail || 'Errore durante l\'annullamento');
    }
  };

  // Grandezze economiche (semantica QE V2 certificata):
  //   Affidato corrente = assigned_cost (commitment contrattuale = subcontract.total_amount_current)
  //   Consuntivato      = actual_cost (== invoiced_cost per i sub, source of truth fatture)
  //   Rate pianificate  = Σ payment_installments programmate
  //   Pagato            = Σ pagamenti effettivi
  const affidato = Number(lineInfo?.assigned_cost ?? line?.assigned_cost ?? 0);
  const consuntivato = Number(lineInfo?.actual_cost ?? line?.actual_cost ?? 0);
  const ratePianificate = Number(summary?.installments_total || 0);
  const pagato = Number(summary?.installments_paid_total || 0);
  const estimated = Number(lineInfo?.estimated_cost ?? line?.estimated_cost ?? 0);
  // Due residui DISTINTI:
  //   Residuo contrattuale = quanto del contratto resta da pagare
  //   Residuo da pianificare = quanto del contratto non è ancora coperto da rate
  const residuoContrattuale = Math.max(affidato - pagato, 0);
  const residuoDaPianificare = Math.max(affidato - ratePianificate, 0);
  const overAllocato = affidato > 0 && ratePianificate > affidato;
  const defaultRata = residuoDaPianificare > 0 ? residuoDaPianificare : (consuntivato > 0 ? consuntivato : estimated);

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Banknote className="w-5 h-5 text-[#FF7819]" />
            Rate pagamento fornitore
          </DialogTitle>
          <DialogDescription>
            {lineInfo?.description || 'Gestione rate'} 
            {lineInfo?.supplier_name && ` - ${lineInfo.supplier_name}`}
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4 py-4">
          {/* Riepilogo economico (Affidato / Consuntivato / Rate pianificate / Pagato-Residuo) */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3 p-4 bg-gray-50 rounded-lg" data-testid="installments-summary">
            <div>
              <p className="text-xs text-gray-500">Affidato corrente</p>
              <p className="font-semibold text-[#B25400]" data-testid="inst-affidato">{formatCurrency(affidato)}</p>
            </div>
            <div>
              <p className="text-xs text-gray-500">Consuntivato</p>
              <p className="font-semibold text-blue-700" data-testid="inst-consuntivato">{formatCurrency(consuntivato)}</p>
            </div>
            <div>
              <p className="text-xs text-gray-500">Rate pianificate</p>
              <p className="font-semibold" data-testid="inst-rate-pianificate">{formatCurrency(ratePianificate)}</p>
            </div>
            <div>
              <p className="text-xs text-gray-500">Pagato / Residuo contratt.</p>
              <p className="font-semibold">
                <span className="text-green-600" data-testid="inst-pagato">{formatCurrency(pagato)}</span>
                {' / '}
                <span className="text-orange-600" data-testid="inst-residuo-contrattuale">{formatCurrency(residuoContrattuale)}</span>
              </p>
            </div>
          </div>

          {/* Warning: rate pianificate superano l'affidato corrente */}
          {overAllocato && (
            <div className="flex items-start gap-2 p-3 rounded-lg bg-orange-50" data-testid="inst-over-allocato-warning">
              <AlertTriangle className="w-5 h-5 flex-shrink-0 text-orange-600" />
              <div className="text-sm">
                <p className="font-medium">Le rate pianificate superano l'affidato corrente</p>
                <p className="text-gray-600">
                  Rate pianificate {formatCurrency(ratePianificate)} · Affidato {formatCurrency(affidato)} · Eccedenza {formatCurrency(ratePianificate - affidato)}
                </p>
              </div>
            </div>
          )}

          {/* Azioni */}
          <div className="flex items-center gap-3 flex-wrap">
            <Button 
              size="sm" 
              onClick={() => { resetForm(); setShowAddForm(true); }}
              className="bg-[#FF7819] hover:bg-[#e56a10]"
            >
              <Plus className="w-4 h-4 mr-1" />
              Aggiungi rata
            </Button>
            {installments.filter(i => i.status !== 'annullata').length === 0 && (
              <Button 
                size="sm" 
                variant="outline"
                onClick={handleCreateDefault}
                disabled={saving || defaultRata <= 0}
                data-testid="inst-create-default-btn"
              >
                <CreditCard className="w-4 h-4 mr-1" />
                Crea rata residua
              </Button>
            )}
            <span className="text-xs text-gray-500 ml-auto" data-testid="inst-residuo-da-pianificare">
              Residuo da pianificare: <span className="font-semibold text-[#B25400]">{formatCurrency(residuoDaPianificare)}</span>
            </span>
          </div>

          {/* Form aggiungi/modifica rata */}
          {showAddForm && (
            <div className="border rounded-lg p-4 bg-gray-50 space-y-4">
              <div className="flex justify-between items-center">
                <h4 className="font-medium">
                  {editingInstallment ? 'Modifica rata' : 'Nuova rata'}
                </h4>
                <Button variant="ghost" size="sm" onClick={resetForm}>
                  <X className="w-4 h-4" />
                </Button>
              </div>
              
              {/* Descrizioni suggerite */}
              <div className="flex flex-wrap gap-1">
                <span className="text-xs text-gray-500 mr-2">Suggerimenti:</span>
                {[...SUGGESTED_DESCRIPTIONS.lavori, ...SUGGESTED_DESCRIPTIONS.forniture.slice(0, 2), ...SUGGESTED_DESCRIPTIONS.generici.slice(0, 2)].map(desc => (
                  <Button
                    key={desc}
                    variant="outline"
                    size="sm"
                    className="h-6 text-xs"
                    onClick={() => setFormData(prev => ({ ...prev, description: desc }))}
                  >
                    {desc}
                  </Button>
                ))}
              </div>
              
              <div className="grid grid-cols-2 gap-4">
                <div className="col-span-2">
                  <Label>Descrizione *</Label>
                  <Input 
                    value={formData.description}
                    onChange={(e) => setFormData(prev => ({ ...prev, description: e.target.value }))}
                    placeholder="Es. Acconto lavori, SAL impresa..."
                  />
                </div>
                
                <div>
                  <Label>Importo (EUR) *</Label>
                  <Input 
                    value={formData.amount}
                    onChange={(e) => setFormData(prev => ({ ...prev, amount: e.target.value }))}
                    placeholder="0,00"
                  />
                </div>
                
                <div>
                  <Label>Scadenza</Label>
                  <Input 
                    type="date"
                    value={formData.due_date}
                    onChange={(e) => setFormData(prev => ({ ...prev, due_date: e.target.value }))}
                  />
                </div>
                
                <div>
                  <Label>Importo pagato (EUR)</Label>
                  <Input 
                    value={formData.paid_amount}
                    onChange={(e) => setFormData(prev => ({ ...prev, paid_amount: e.target.value }))}
                    placeholder="0,00"
                  />
                </div>
                
                <div>
                  <Label>Data pagamento</Label>
                  <Input 
                    type="date"
                    value={formData.paid_at}
                    onChange={(e) => setFormData(prev => ({ ...prev, paid_at: e.target.value }))}
                  />
                </div>
                
                <div>
                  <Label>N. Fattura</Label>
                  <Input 
                    value={formData.invoice_number}
                    onChange={(e) => setFormData(prev => ({ ...prev, invoice_number: e.target.value }))}
                    placeholder="FT-2026/001"
                  />
                </div>
                
                <div>
                  <Label>Data fattura</Label>
                  <Input 
                    type="date"
                    value={formData.invoice_date}
                    onChange={(e) => setFormData(prev => ({ ...prev, invoice_date: e.target.value }))}
                  />
                </div>
                
                {linkedDocuments.length > 0 && (
                  <div className="col-span-2">
                    <Label>Documento collegato</Label>
                    <Select 
                      value={formData.linked_document_id}
                      onValueChange={(v) => setFormData(prev => ({ ...prev, linked_document_id: v }))}
                    >
                      <SelectTrigger>
                        <SelectValue placeholder="Seleziona documento..." />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="">Nessuno</SelectItem>
                        {linkedDocuments.map(doc => (
                          <SelectItem key={doc.id} value={doc.id}>
                            {doc.original_filename}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                )}
                
                <div className="col-span-2">
                  <Label>Note</Label>
                  <Textarea 
                    value={formData.notes}
                    onChange={(e) => setFormData(prev => ({ ...prev, notes: e.target.value }))}
                    rows={2}
                    placeholder="Note aggiuntive..."
                  />
                </div>
              </div>
              
              <Button 
                onClick={handleSaveInstallment}
                disabled={saving}
                className="w-full bg-[#FF7819] hover:bg-[#e56a10]"
              >
                {saving ? (
                  <>
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    Salvataggio...
                  </>
                ) : (
                  <>
                    <Check className="w-4 h-4 mr-2" />
                    {editingInstallment ? 'Salva modifiche' : 'Crea rata'}
                  </>
                )}
              </Button>
            </div>
          )}

          {/* Lista rate */}
          <div className="border rounded-lg">
            <div className="bg-gray-100 px-4 py-2 border-b flex justify-between items-center">
              <h4 className="font-medium text-sm">
                Rate ({installments.filter(i => i.status !== 'annullata').length})
              </h4>
              {summary && (
                <Badge className={PAYMENT_STATUS_COLORS[summary.payment_status]}>
                  {PAYMENT_STATUS_LABELS[summary.payment_status]}
                </Badge>
              )}
            </div>
            
            {loading ? (
              <div className="flex items-center justify-center py-8">
                <Loader2 className="w-6 h-6 animate-spin text-gray-400" />
              </div>
            ) : installments.length === 0 ? (
              <div className="text-center py-8 text-gray-500">
                <Calendar className="w-8 h-8 mx-auto mb-2 text-gray-300" />
                <p className="text-sm">Nessuna rata definita</p>
                <p className="text-xs text-gray-400 mt-1">
                  Aggiungi rate per tracciare i pagamenti verso il fornitore
                </p>
              </div>
            ) : (
              <div className="divide-y">
                {installments.map(inst => {
                  const residuo = inst.amount - inst.paid_amount;
                  const isAnnullata = inst.status === 'annullata';
                  
                  return (
                    <div 
                      key={inst.id} 
                      className={`p-3 ${isAnnullata ? 'bg-gray-50 opacity-60' : 'hover:bg-gray-50'}`}
                    >
                      <div className="flex items-start justify-between gap-4">
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2">
                            <p className={`font-medium ${isAnnullata ? 'line-through' : ''}`}>
                              {inst.description}
                            </p>
                            <Badge className={`text-xs ${INSTALLMENT_STATUS_COLORS[inst.status]}`}>
                              {INSTALLMENT_STATUS_LABELS[inst.status]}
                            </Badge>
                          </div>
                          <div className="flex flex-wrap gap-x-4 gap-y-1 mt-1 text-sm text-gray-600">
                            <span>
                              <strong>{formatCurrency(inst.amount)}</strong>
                            </span>
                            {inst.paid_amount > 0 && (
                              <span className="text-green-600">
                                Pagato: {formatCurrency(inst.paid_amount)}
                              </span>
                            )}
                            {residuo > 0 && !isAnnullata && (
                              <span className="text-orange-600">
                                Residuo: {formatCurrency(residuo)}
                              </span>
                            )}
                            {inst.due_date && (
                              <span className={inst.status === 'scaduta' ? 'text-red-600' : ''}>
                                <Clock className="w-3 h-3 inline mr-1" />
                                {formatDate(inst.due_date)}
                              </span>
                            )}
                            {inst.invoice_number && (
                              <span>Fatt. {inst.invoice_number}</span>
                            )}
                          </div>
                          {inst.notes && (
                            <p className="text-xs text-gray-400 mt-1 truncate">{inst.notes}</p>
                          )}
                        </div>
                        
                        {!isAnnullata && (
                          <div className="flex items-center gap-1">
                            {/* Form pagamento inline */}
                            {showPayForm === inst.id ? (
                              <div className="flex items-center gap-2 bg-green-50 p-2 rounded">
                                <Input 
                                  className="w-24 h-8 text-sm"
                                  placeholder="Importo"
                                  value={payAmount}
                                  onChange={(e) => setPayAmount(e.target.value)}
                                />
                                <Input 
                                  type="date"
                                  className="w-32 h-8 text-sm"
                                  value={payDate}
                                  onChange={(e) => setPayDate(e.target.value)}
                                />
                                <Button 
                                  size="sm" 
                                  className="h-8 bg-green-600 hover:bg-green-700"
                                  onClick={() => handleMarkPaid(inst)}
                                  disabled={saving}
                                >
                                  <Check className="w-4 h-4" />
                                </Button>
                                <Button 
                                  size="sm" 
                                  variant="ghost"
                                  className="h-8"
                                  onClick={() => { setShowPayForm(null); setPayAmount(''); setPayDate(''); }}
                                >
                                  <X className="w-4 h-4" />
                                </Button>
                              </div>
                            ) : (
                              <>
                                {inst.status !== 'pagata' && (
                                  <Button 
                                    variant="ghost" 
                                    size="sm"
                                    className="text-green-600 hover:text-green-700"
                                    onClick={() => { 
                                      setShowPayForm(inst.id); 
                                      setPayAmount(String(residuo)); 
                                      setPayDate(new Date().toISOString().split('T')[0]);
                                    }}
                                    title="Registra pagamento"
                                  >
                                    <DollarSign className="w-4 h-4" />
                                  </Button>
                                )}
                                <Button 
                                  variant="ghost" 
                                  size="sm"
                                  onClick={() => openEditForm(inst)}
                                  title="Modifica"
                                >
                                  <Edit className="w-4 h-4" />
                                </Button>
                                <Button 
                                  variant="ghost" 
                                  size="sm"
                                  className="text-red-600 hover:text-red-700"
                                  onClick={() => handleCancelInstallment(inst)}
                                  title="Annulla"
                                >
                                  <Trash2 className="w-4 h-4" />
                                </Button>
                              </>
                            )}
                          </div>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>

          {/* Info */}
          <div className="flex items-start gap-2 text-xs text-gray-500 bg-blue-50 p-3 rounded">
            <AlertCircle className="w-4 h-4 text-blue-500 flex-shrink-0 mt-0.5" />
            <p>
              Le rate di pagamento sono indipendenti dai SAL cliente e dalle milestone di progetto. 
              Servono a tracciare i pagamenti verso fornitori e subappaltatori.
            </p>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default EconomicLineInstallmentsModal;
